import React, { createContext, useContext, useEffect, useState } from "react";
import { api, setToken, clearToken, getToken } from "@/lib/api";

const AuthCtx = createContext(null);

export const homePath = (u) => (u?.is_admin ? "/admin" : u?.role === "developer" ? "/dashboard" : "/home");

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // CRITICAL: If returning from Google OAuth callback, skip the /me check.
    // AuthCallback handles the session exchange first.
    if (window.location.pathname === "/auth-callback" || window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    (async () => {
      const t = getToken();
      if (!t) {
        setLoading(false);
        return;
      }
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        clearToken();
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function login(email, password) {
    const { data } = await api.post("/auth/login", { email, password });
    setToken(data.access_token);
    setUser(data.user);
    return data.user;
  }

  async function register(payload) {
    // Server now sends a verification email — DO NOT auto-login.
    const { data } = await api.post("/auth/register", payload);
    return data;
  }

  async function verifyEmail(token) {
    const { data } = await api.post("/auth/verify-email", { token });
    setToken(data.access_token);
    setUser(data.user);
    return data.user;
  }

  async function resendVerification(email) {
    await api.post("/auth/resend-verification", { email });
  }

  async function forgotPassword(email) {
    await api.post("/auth/forgot-password", { email });
  }

  async function resetPassword(token, password) {
    const { data } = await api.post("/auth/reset-password", { token, password });
    setToken(data.access_token);
    setUser(data.user);
    return data.user;
  }

  async function changePassword(current_password, new_password) {
    await api.post("/auth/change-password", { current_password, new_password });
  }

  async function changeEmail(new_email, current_password) {
    const { data } = await api.post("/auth/change-email", { new_email, current_password });
    // Server marks email_verified=false and sends a fresh verification email;
    // don't overwrite `user` here — the profile page reflects the pending state itself.
    return data;
  }

  async function deleteAccount(current_password, confirmation) {
    await api.delete("/auth/me", { data: { current_password, confirmation } });
    setToken(null);
    setUser(null);
  }

  async function logout() {
    try {
      await api.post("/auth/logout");
    } catch { /* ignore */ }
    clearToken();
    setUser(null);
  }

  async function updateProfile(payload) {
    const { data } = await api.patch("/auth/me", payload);
    setUser(data);
    return data;
  }

  async function refreshUser() {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch { /* ignore */ }
  }

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout, updateProfile, refreshUser, setUser, verifyEmail, resendVerification, forgotPassword, resetPassword, changePassword, changeEmail, deleteAccount }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
